import type { TFunction } from "i18next"
import type * as React from "react"

// Values are out of display order; list statuses with MILESTONE_STATUS_ORDER, never by value.
const MILESTONE_STATUSES = {
  PLANNED: 1,
  PENDING: 5,
  IN_PROGRESS: 2,
  COMPLETED: 3,
  BLOCKED: 4
} as const

type MilestoneStatusesKeys = keyof typeof MILESTONE_STATUSES
type MilestoneStatuses = (typeof MILESTONE_STATUSES)[MilestoneStatusesKeys]

const MILESTONE_STATUS_ORDER: readonly MilestoneStatuses[] = [
  MILESTONE_STATUSES.PLANNED,
  MILESTONE_STATUSES.PENDING,
  MILESTONE_STATUSES.IN_PROGRESS,
  MILESTONE_STATUSES.COMPLETED,
  MILESTONE_STATUSES.BLOCKED
]

const MILESTONE_STATUSES_MAP: Record<
  MilestoneStatuses,
  {
    id: MilestoneStatuses
    value: MilestoneStatuses
    labelKey: string
    tooltipKey: string
    style: React.CSSProperties
  }
> = {
  [MILESTONE_STATUSES.PLANNED]: {
    id: MILESTONE_STATUSES.PLANNED,
    value: MILESTONE_STATUSES.PLANNED,
    labelKey: "milestoneStatus.label.planned",
    tooltipKey: "milestoneStatus.tooltip.planned",
    style: {
      color: "#616d73",
      backgroundColor: "#ECEFF1",
      borderColor: "#ECEFF1"
    }
  },
  [MILESTONE_STATUSES.PENDING]: {
    id: MILESTONE_STATUSES.PENDING,
    value: MILESTONE_STATUSES.PENDING,
    labelKey: "milestoneStatus.label.pending",
    tooltipKey: "milestoneStatus.tooltip.pending",
    style: {
      color: "#AD6800",
      backgroundColor: "#FFFBE6",
      borderColor: "#FFFBE6"
    }
  },
  [MILESTONE_STATUSES.IN_PROGRESS]: {
    id: MILESTONE_STATUSES.IN_PROGRESS,
    value: MILESTONE_STATUSES.IN_PROGRESS,
    labelKey: "milestoneStatus.label.inProgress",
    tooltipKey: "milestoneStatus.tooltip.inProgress",
    style: {
      color: "#FF9800",
      backgroundColor: "#FFF3E0",
      borderColor: "#FFF3E0"
    }
  },
  [MILESTONE_STATUSES.COMPLETED]: {
    id: MILESTONE_STATUSES.COMPLETED,
    value: MILESTONE_STATUSES.COMPLETED,
    labelKey: "milestoneStatus.label.completed",
    tooltipKey: "milestoneStatus.tooltip.completed",
    style: {
      color: "#4CAF50",
      backgroundColor: "#E8F5E9",
      borderColor: "#E8F5E9"
    }
  },
  [MILESTONE_STATUSES.BLOCKED]: {
    id: MILESTONE_STATUSES.BLOCKED,
    value: MILESTONE_STATUSES.BLOCKED,
    labelKey: "milestoneStatus.label.blocked",
    tooltipKey: "milestoneStatus.tooltip.blocked",
    style: {
      color: "#F44336",
      backgroundColor: "#FFEBEE",
      borderColor: "#FFEBEE"
    }
  }
}

const milestoneStatusOptions = (
  t: TFunction
): {
  label: string
  value: MilestoneStatuses
}[] =>
  MILESTONE_STATUS_ORDER.map((status) => ({
    label: t(MILESTONE_STATUSES_MAP[status].labelKey),
    value: status
  }))

export {
  MILESTONE_STATUS_ORDER,
  MILESTONE_STATUSES,
  MILESTONE_STATUSES_MAP,
  milestoneStatusOptions,
  type MilestoneStatuses
}
